import React from 'react';
import { AlertCircle, RefreshCw, Wifi, Database, Mic, Check, X, HelpCircle, ExternalLink, ArrowRight } from 'lucide-react';
import { Button } from '../ui/Button';

const AIVoiceAgentTroubleshooting: React.FC = () => {
  return (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
      <h2 className="text-xl font-semibold mb-6 flex items-center">
        <HelpCircle className="h-6 w-6 text-primary mr-2" />
        LeadWealth.ai Voice Agents Troubleshooting Guide
      </h2>

      <div className="space-y-6">
        {/* Connection Verification */}
        <div className="border rounded-lg p-4">
          <h3 className="text-lg font-medium mb-4">Connection Verification</h3>
          <p className="text-gray-600 mb-4">
            When accessing the Settings tab in LeadWealth.ai Voice Agents, verify that the following connection button is displayed:
          </p>
          <div className="space-y-3 ml-4">
            <div className="flex items-center">
              <Check className="h-5 w-5 text-green-500 mr-2" />
              <span className="text-gray-700">LeadWealth.ai Voice Agent Connection</span>
            </div>
          </div>
        </div>

        {/* Blank Screen Issues */}
        <div className="border rounded-lg p-4">
          <h3 className="text-lg font-medium mb-4">Blank Screen Issues</h3>
          <p className="text-gray-600 mb-4">
            If you see a blank screen when accessing the LeadWealth.ai Voice Agents settings, try the following steps:
          </p>
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="bg-primary/10 rounded-full p-1 mr-3 mt-0.5">
                <RefreshCw className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="font-medium text-gray-800">Refresh the page</p>
                <p className="text-sm text-gray-600">Sometimes a simple page refresh can resolve loading issues.</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-primary/10 rounded-full p-1 mr-3 mt-0.5">
                <Database className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="font-medium text-gray-800">Clear your browser cache</p>
                <p className="text-sm text-gray-600">Cached data might be preventing the page from loading correctly.</p>
                <div className="mt-2 text-xs text-gray-500">
                  <p>Chrome: Settings → Privacy and security → Clear browsing data</p>
                  <p>Firefox: Options → Privacy & Security → Cookies and Site Data → Clear Data</p>
                  <p>Safari: Preferences → Privacy → Manage Website Data → Remove All</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-primary/10 rounded-full p-1 mr-3 mt-0.5">
                <Wifi className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="font-medium text-gray-800">Ensure you have a stable internet connection</p>
                <p className="text-sm text-gray-600">Voice agent settings require an active internet connection to load properly.</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-primary/10 rounded-full p-1 mr-3 mt-0.5">
                <Mic className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="font-medium text-gray-800">Verify you have the necessary permissions</p>
                <p className="text-sm text-gray-600">You may need administrator access to view and modify voice agent settings.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Connection Button Verification */}
        <div className="border rounded-lg p-4">
          <h3 className="text-lg font-medium mb-4">Connection Button Verification</h3>
          <p className="text-gray-600 mb-4">
            Each connection button should have the following characteristics:
          </p>
          <div className="space-y-4">
            <div className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
              <div>
                <p className="font-medium text-gray-800">Clearly visible</p>
                <p className="text-sm text-gray-600">Buttons should be prominently displayed in the settings interface.</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
              <div>
                <p className="font-medium text-gray-800">Show current connection status</p>
                <p className="text-sm text-gray-600">Each button should indicate whether the service is connected or disconnected.</p>
                <div className="mt-2 flex space-x-4">
                  <div className="flex items-center">
                    <div className="h-3 w-3 rounded-full bg-green-500 mr-1"></div>
                    <span className="text-xs">Connected</span>
                  </div>
                  <div className="flex items-center">
                    <div className="h-3 w-3 rounded-full bg-gray-400 mr-1"></div>
                    <span className="text-xs">Disconnected</span>
                  </div>
                  <div className="flex items-center">
                    <div className="h-3 w-3 rounded-full bg-red-500 mr-1"></div>
                    <span className="text-xs">Error</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
              <div>
                <p className="font-medium text-gray-800">Provide clear options to connect/disconnect</p>
                <p className="text-sm text-gray-600">Buttons should change state and label based on connection status.</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
              <div>
                <p className="font-medium text-gray-800">Display relevant API keys or authentication status</p>
                <p className="text-sm text-gray-600">When connected, partial API keys or authentication details should be visible.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Common Error Messages */}
        <div className="border rounded-lg p-4">
          <h3 className="text-lg font-medium mb-4">Common Error Messages</h3>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">Authentication Failed</p>
                  <p className="text-sm text-red-700">The API key provided is invalid or has expired.</p>
                  <p className="text-xs text-red-600 mt-1">Solution: Generate a new API key from the provider's dashboard and update it in the settings.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">Connection Timeout</p>
                  <p className="text-sm text-red-700">The system couldn't establish a connection with the voice agent provider.</p>
                  <p className="text-xs text-red-600 mt-1">Solution: Check your internet connection and try again. If the issue persists, the service might be temporarily unavailable.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">Insufficient Permissions</p>
                  <p className="text-sm text-red-700">Your account doesn't have the necessary permissions to modify voice agent settings.</p>
                  <p className="text-xs text-red-600 mt-1">Solution: Contact your administrator to request the appropriate permissions.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-yellow-500 mr-2 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-800">Service Limit Reached</p>
                  <p className="text-sm text-yellow-700">You've reached the maximum number of voice agents allowed for your subscription.</p>
                  <p className="text-xs text-yellow-600 mt-1">Solution: Upgrade your subscription or deactivate unused voice agents to free up capacity.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Diagnostic Steps */}
        <div className="border rounded-lg p-4">
          <h3 className="text-lg font-medium mb-4">Diagnostic Steps</h3>
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="bg-gray-100 rounded-full h-6 w-6 flex items-center justify-center mr-3 mt-0.5">
                <span className="text-gray-700 font-medium">1</span>
              </div>
              <div>
                <p className="font-medium text-gray-800">Check browser console for errors</p>
                <p className="text-sm text-gray-600 mb-1">Open your browser's developer tools to check for JavaScript errors.</p>
                <div className="text-xs text-gray-500">
                  <p>Chrome/Edge: Press F12 or right-click → Inspect → Console tab</p>
                  <p>Firefox: Press F12 or right-click → Inspect Element → Console tab</p>
                  <p>Safari: Enable developer menu, then Develop → Show JavaScript Console</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-gray-100 rounded-full h-6 w-6 flex items-center justify-center mr-3 mt-0.5">
                <span className="text-gray-700 font-medium">2</span>
              </div>
              <div>
                <p className="font-medium text-gray-800">Verify network requests</p>
                <p className="text-sm text-gray-600">Check if API requests to voice agent providers are being made and their responses.</p>
                <p className="text-xs text-gray-500 mt-1">In developer tools, go to the Network tab and look for requests to the voice agent API endpoints.</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-gray-100 rounded-full h-6 w-6 flex items-center justify-center mr-3 mt-0.5">
                <span className="text-gray-700 font-medium">3</span>
              </div>
              <div>
                <p className="font-medium text-gray-800">Test with a different browser</p>
                <p className="text-sm text-gray-600">Try accessing the settings in a different browser to rule out browser-specific issues.</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-gray-100 rounded-full h-6 w-6 flex items-center justify-center mr-3 mt-0.5">
                <span className="text-gray-700 font-medium">4</span>
              </div>
              <div>
                <p className="font-medium text-gray-800">Check for browser extensions</p>
                <p className="text-sm text-gray-600">Disable browser extensions that might be interfering with the page functionality.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Support */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <HelpCircle className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-blue-800">Still having issues?</h4>
              <p className="mt-1 text-sm text-blue-700">
                If you've tried the troubleshooting steps above and are still experiencing issues, please contact our support team with the following information:
              </p>
              <ul className="mt-2 text-sm text-blue-700 list-disc list-inside space-y-1">
                <li>Your browser and version</li>
                <li>Operating system</li>
                <li>Exact error messages (screenshots if possible)</li>
                <li>Steps you've already taken to troubleshoot</li>
                <li>Description of what you see or don't see on the screen</li>
              </ul>
              <div className="mt-3">
                <Button className="flex items-center">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Contact Support
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Fix Button */}
        <div className="flex justify-center mt-6">
          <Button 
            className="flex items-center"
            onClick={() => window.location.href = '/dashboard/voice-agents'}
          >
            Go to Voice Agents Dashboard
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AIVoiceAgentTroubleshooting;